import React from "react";
import { Box, Typography, Avatar, Checkbox } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTheme, alpha } from "@mui/material/styles";
import AhaRosterCell from "../../../components/aha/AhaRosterCell";
import { DS, AVATAR_GRADIENTS, getInitials } from "./rosterDesign";
import { RESULT_LABEL } from "./rosterModel";

const ROSTER_GRID =
  "24px minmax(150px, 1.5fr) minmax(150px, 1.2fr) minmax(110px, 1fr) minmax(140px, 1.1fr) minmax(90px, .8fr) 190px";
// AHA classes add an "AHA eCard" column before Actions.
const ROSTER_GRID_AHA =
  "24px minmax(150px, 1.4fr) minmax(150px, 1.1fr) minmax(110px, .9fr) minmax(120px, 1fr) minmax(90px, .7fr) minmax(210px, 1.5fr) 190px";
export const rosterGrid = (ahaEnabled) => (ahaEnabled ? ROSTER_GRID_AHA : ROSTER_GRID);

const SELECT_SX = { p: 0, "& .MuiSvgIcon-root": { fontSize: 20 } };

const ACTION_BTN = {
  width: 30, height: 30, borderRadius: "7px",
  border: "1px solid transparent", bgcolor: "transparent", color: DS.ink3,
  cursor: "pointer", display: "grid", placeItems: "center",
  transition: "all .12s",
  "&:hover": { bgcolor: "var(--color-surface-canvas-light)", borderColor: DS.line, color: DS.ink },
};

export function StatusPill({ type }) {
  const theme = useTheme();
  const tAccent = theme.palette.warning.main;
  const cfg = {
    signed: { bg: DS.success, color: "#fff", icon: <CheckIcon sx={{ fontSize: 11 }} /> },
    ready: { bg: DS.blue, color: "#fff" },
    failed: { bg: DS.danger, color: "#fff", icon: <CloseIcon sx={{ fontSize: 11 }} /> },
    progress: { bg: DS.greyBg, color: DS.ink },
    "signed-in": { bg: DS.liveBg, color: DS.live, pulse: true },
    "on-track": { bg: DS.greyBg, color: DS.ink },
    behind: { bg: alpha(tAccent, 0.1), color: tAccent },
    "at-risk": { bg: DS.danger50, color: DS.danger },
  }[type] || { bg: DS.greyBg, color: DS.ink };

  return (
    <Box
      data-testid={`roster-status-${type}`}
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75, height: 24, px: 1.25, borderRadius: "999px",
        fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", bgcolor: cfg.bg, color: cfg.color,
      }}
    >
      {cfg.icon || (
        <Box
          sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", position: "relative",
            ...(cfg.pulse
              ? {
                  "&::before": {
                    content: '""', position: "absolute", inset: -3, borderRadius: "50%", bgcolor: DS.live,
                    opacity: 0.35, animation: "roster-pulse 1.6s ease-out infinite",
                  },
                }
              : {}),
          }}
        />
      )}
      {RESULT_LABEL[type] || "Active"}
    </Box>
  );
}

export function ColHead({ ahaEnabled = false, selectAll }) {
  const headStyle = {
    fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DS.ink3,
  };
  return (
    <Box
      sx={{
        display: "grid", gridTemplateColumns: rosterGrid(ahaEnabled), gap: "14px", alignItems: "center",
        px: "18px", py: "10px", borderBottom: `1px solid ${DS.line2}`, bgcolor: "var(--color-surface-canvas-light)",
      }}
    >
      {selectAll ? (
        <Checkbox size="small" sx={SELECT_SX} checked={selectAll.checked} indeterminate={selectAll.indeterminate}
          disabled={selectAll.disabled} onChange={selectAll.onChange}
          inputProps={{ "aria-label": "Select all students shown for sign-off" }} />
      ) : <Box />}
      <Typography sx={headStyle}>Student</Typography>
      <Typography sx={headStyle}>Status</Typography>
      <Typography sx={headStyle}>Attendance</Typography>
      <Typography sx={headStyle}>Skill Checklists</Typography>
      <Typography sx={headStyle}>Test Avg</Typography>
      {ahaEnabled && <Typography sx={headStyle}>AHA eCard</Typography>}
      <Typography sx={{ ...headStyle, textAlign: "right" }}>Actions</Typography>
    </Box>
  );
}

/** A section heading in the sign-off queue ("Ready to sign off · 3"). */
export function GroupHeader({ group }) {
  return (
    <Box
      data-testid={`roster-group-${group.key}`}
      sx={{
        display: "flex", alignItems: "center", gap: 1, px: "18px", py: "8px", bgcolor: DS.greyBg,
        borderBottom: `1px solid ${DS.line3}`, fontSize: 11, fontWeight: 700, letterSpacing: ".08em",
        textTransform: "uppercase", color: DS.ink,
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: group.color }} />
      {group.label}
      <Box component="span" sx={{ fontSize: 11, px: "6px", borderRadius: "4px", bgcolor: "var(--color-surface-canvas-light)" }}>
        {group.items.length}
      </Box>
    </Box>
  );
}

export function RosterRow({
  reg, index, result, note, onProfileClick, onDm, onSignOff, aha, ahaEnabled = false, classId,
  canResolveAha = false, onAhaResolved, selected = false, onToggleSelect,
}) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const tAccent = theme.palette.warning.main;
  const userId = String(reg.userId || "");
  const name = reg.user?.name || reg.user?.username || `Student ${reg.userId}`;
  const email = reg.user?.email || "";
  const avatarGrad = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  const isAtRisk = result === "at-risk";
  const isFailed = result === "failed";

  const sessionsTotal = reg.sessionsTotal ?? 0;
  const sessionsAttended = reg.sessionsAttended ?? 0;
  const sessionsSoFar = reg.sessionsSoFar ?? 0;
  const attendancePct = sessionsSoFar > 0 ? Math.round((sessionsAttended / sessionsSoFar) * 100) : null;
  const missed = Math.max(sessionsSoFar - sessionsAttended, 0);

  const skillsTotal = reg.skillsTotal ?? 0;
  const skillsCompleted = reg.skillsCompleted ?? 0;
  const skillsPct = skillsTotal > 0 ? (skillsCompleted / skillsTotal) * 100 : 0;
  const skillsBadgeCfg = {
    DONE: { label: "DONE", color: tSecondary, bg: alpha(tSecondary, 0.12) },
    NEEDS_REEVAL: { label: "NEEDS REEVAL", color: DS.danger, bg: DS.danger50 },
  }[reg.skillsBadge] || { label: reg.skillsBadge, color: tAccent, bg: alpha(tAccent, 0.12) };

  const testAvgPercent = reg.testAvgPercent;
  const testsPassed = reg.testsPassed ?? 0;
  const testsTotal = reg.testsTotal ?? 0;

  const ahaIssued = ahaEnabled && aha?.issuanceState === "ASSIGNED";
  const rowTint = ahaIssued ? "var(--color-surface-canvas-light)" : "transparent";
  const open = () => onProfileClick(reg.userId, name);

  return (
    <Box
      onClick={open}
      sx={{
        display: "grid", gridTemplateColumns: rosterGrid(ahaEnabled), gap: "14px", alignItems: "center",
        px: "18px", py: "14px", borderBottom: `1px solid ${DS.line3}`, cursor: "pointer", transition: "background .12s",
        background: isAtRisk || isFailed ? "linear-gradient(90deg, var(--color-danger-bg), transparent 30%)" : rowTint,
        "&:hover": {
          background: isAtRisk || isFailed
            ? "linear-gradient(90deg, var(--color-danger-bg), rgba(250,248,244,1))"
            : DS.surface2,
        },
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Checkbox size="small" sx={SELECT_SX} checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggleSelect?.(reg.userId, name)}
        inputProps={{ "aria-label": `Select ${name} for sign-off` }} />

      {/* Student */}
      <Box sx={{ display: "grid", gridTemplateColumns: "36px 1fr", gap: "12px", alignItems: "center", minWidth: 0 }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: "12px", fontWeight: 700, background: avatarGrad, letterSpacing: "-.01em" }}>
          {getInitials(name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink, mb: "2px", letterSpacing: "-.005em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: "11.5px", color: DS.ink3, fontWeight: 500, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {email}
          </Typography>
        </Box>
      </Box>

      {/* Status, plus why a student failed or is at risk */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
        <span data-testid={result === "ready" ? `ready-to-sign-off-${userId}` : undefined}
          title={result === "ready" ? "All sessions attended, tests and required skill checks passed" : undefined}>
          <StatusPill type={result} />
        </span>
        {note && (
          <Typography sx={{ fontSize: "11.5px", color: isFailed ? DS.danger : tAccent }}>{note}</Typography>
        )}
      </Box>

      {/* Attendance */}
      <Box>
        {sessionsTotal > 0 ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: DS.ink }}>
              {sessionsAttended} / {sessionsTotal}
              <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: DS.ink3, ml: "6px" }}>
                {attendancePct != null
                  ? missed > 0 ? `${attendancePct}% (${missed} missed)` : `${attendancePct}% so far`
                  : "—"}
              </Box>
            </Typography>
            <Box sx={{ height: 4, borderRadius: "999px", bgcolor: DS.line3, mt: "5px", overflow: "hidden", maxWidth: 90 }}>
              <Box sx={{ height: "100%", borderRadius: "999px", width: `${attendancePct ?? 0}%`,
                bgcolor: attendancePct != null && attendancePct < 75 ? DS.danger : tSecondary }} />
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: DS.ink3 }}>—</Typography>
        )}
      </Box>

      {/* Skill Checklists */}
      <Box>
        {skillsTotal > 0 ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: DS.ink }}>
                {skillsCompleted} / {skillsTotal} skills
              </Typography>
              <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".03em", px: "6px", py: "1.5px",
                borderRadius: "999px", color: skillsBadgeCfg.color, bgcolor: skillsBadgeCfg.bg, whiteSpace: "nowrap" }}>
                {skillsBadgeCfg.label}
              </Box>
            </Box>
            <Box sx={{ height: 4, borderRadius: "999px", bgcolor: DS.line3, mt: "5px", overflow: "hidden", maxWidth: 90 }}>
              <Box sx={{ height: "100%", borderRadius: "999px", width: `${skillsPct}%`,
                bgcolor: reg.skillsBadge === "NEEDS_REEVAL" ? DS.danger : tSecondary }} />
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: DS.ink3 }}>—</Typography>
        )}
      </Box>

      {/* Test Avg */}
      <Box>
        {testAvgPercent != null ? (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: testAvgPercent >= 70 ? tSecondary : DS.danger }}>
              {testAvgPercent}%
            </Typography>
            <Typography sx={{ fontSize: 11, color: DS.ink3, fontWeight: 600 }}>
              {testsPassed} OF {testsTotal} PASSED
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: DS.ink3 }}>—</Typography>
        )}
      </Box>

      {/* AHA eCard */}
      {ahaEnabled && (
        <Box sx={{ minWidth: 0 }}>
          <AhaRosterCell item={aha} classId={classId} canResolve={canResolveAha} onResolved={onAhaResolved} />
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center" }}>
        {result === "ready" && (
          <Box component="button" type="button" data-testid={`sign-off-${userId}`}
            onClick={(e) => { e.stopPropagation(); onSignOff?.([{ userId: userId, name }]); }}
            sx={{ display: "inline-flex", alignItems: "center", gap: "6px", height: 28, px: "10px", mr: 0.5,
              border: 0, borderRadius: "8px", bgcolor: DS.blue, color: "#fff", fontFamily: "inherit", fontSize: 11,
              fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap",
              "&:hover": { bgcolor: DS.blueDark } }}>
            <EditOutlinedIcon sx={{ fontSize: 13 }} />
            Sign off
          </Box>
        )}
        <Box component="button" onClick={(e) => { e.stopPropagation(); onDm(reg); }} title="Send message"
          sx={{ ...ACTION_BTN, fontFamily: "inherit" }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
        </Box>
        <Box component="button" onClick={(e) => { e.stopPropagation(); open(); }} title="View profile"
          sx={{ ...ACTION_BTN, fontFamily: "inherit" }}>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </Box>
  );
}
